/**
 * Welcome Modal JavaScript
 * Standalone implementation for the welcome modal
 */

if (!customElements.get('welcome-modal')) {
  class WelcomeModal extends HTMLElement {
    constructor() {
      super();
      this.initializeWelcomeModal();
    }

    /**
     * Initialize welcome modal functionality
     */
    initializeWelcomeModal() {
      this.classList.add('welcome-modal');
      this.isOpen = false;
      
      // Set up trigger functionality
      this.setupTriggers();
      
      // Set up close functionality
      this.setupCloseFunctionality();
    }

    /**
     * Set up trigger functionality
     */
    setupTriggers() {
      const trigger = this.dataset.trigger;
      const delay = parseInt(this.dataset.delay) || 3;
      const showOnMobile = this.dataset.showOnMobile === 'true';
      const cookie = `${this.id}-dismissed`;

      // Check if modal should be shown
      if (this.shouldShowModal(cookie, showOnMobile)) {
        if (trigger === 'delay') {
          setTimeout(() => this.open(), delay * 1000);
        } else if (trigger === 'exit' && window.innerWidth >= 768) {
          this.setupExitIntent();
        } else if (trigger === 'clipboard') {
          this.setupClipboardTrigger();
        }
      }
    }

    /**
     * Check if modal should be shown
     */
    shouldShowModal(cookie, showOnMobile) {
      if (this.getCookie(cookie)) return false;
      if (!showOnMobile && window.innerWidth < 768) return false;
      return true;
    }

    /**
     * Set up exit intent functionality
     */
    setupExitIntent() {
      const handleMouseLeave = (e) => {
        if (e.clientY < 0) {
          this.open();
          document.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    /**
     * Set up clipboard trigger functionality
     */
    setupClipboardTrigger() {
      const handleCopy = () => {
        this.open();
        document.removeEventListener('copy', handleCopy);
      };
      document.addEventListener('copy', handleCopy);
    }

    /**
     * Set up close functionality
     */
    setupCloseFunctionality() {
      // Close button
      const closeBtn = this.querySelector('.welcome-modal__close-btn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Click outside to close
      this.addEventListener('click', (e) => {
        if (e.target === this) {
          this.close();
        }
      });

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    /**
     * Open the modal
     */
    open() {
      this.isOpen = true;
      this.classList.add('visible');
      this.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      
      // Focus management
      const focusableElements = this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    /**
     * Close the modal
     */
    close() {
      this.isOpen = false;
      this.classList.remove('visible');
      this.style.display = 'none';
      document.body.style.overflow = '';
      
      // Set cookie to remember dismissal
      const dismissDays = parseInt(this.dataset.dismissDays) || 7;
      this.setCookie(`${this.id}-dismissed`, 'true', dismissDays);
    }

    /**
     * Set a cookie
     */
    setCookie(name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
      }
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
    }

    /**
     * Get a cookie value
     */
    getCookie(name) {
      const cookieString = `; ${document.cookie}`;
      const cookies = cookieString.split(`; ${name}=`);
      if (cookies.length === 2) {
        return cookies.pop().split(';').shift();
      }
      return null;
    }
  }

  customElements.define('welcome-modal', WelcomeModal);
}
